import Highcharts from 'highcharts';
import moment from 'moment';
import _ from 'lodash';

import chartUtils from './chartUtils';
import naveLogo from '../assets/img/nave-logo.svg';
import poppinsRegular from '../assets/fonts/Poppins/Poppins-Regular.ttf';
import poppinsSemiBold from '../assets/fonts/Poppins/Poppins-SemiBold.ttf';

const CANVAS = { width: 1600, height: 900, scale: 2 };
const LEFT = 64;
const RIGHT = 1536;
const CHART_TOP = 150;
const CHART_AREA = { width: 1472, height: 636 };
const FOOTER_LINE = 810;
const FOOTER_MIDDLE = 839;
const LOGO = { width: 72, height: 72 * 12 / 55 };
const PDF_PAGE = { width: 960, height: 540 };
const NAVY = '#001A33';
const GREY = '#545D66';
const BORDER = '#D5E7F2';
const GRID = '#E6F1F7';
const LEGEND = {
  dot: 10, text: 14, gap: 6, between: 28, row: 26, top: 14, nameChars: 60, charWidth: 0.56, maxRows: 4,
};

const escapeXML = text => String(text == null ? '' : text)
  .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

const toDataURL = blob => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.onload = () => resolve(reader.result);
  reader.onerror = reject;
  reader.readAsDataURL(blob);
});

// an asset the build inlined is already a data URL (the app's security policy doesn't let it be fetched); a file is
const asDataURL = async url => (/^data:/.test(url) ? url : toDataURL(await (await fetch(url)).blob()));

async function fontFaces() {
  const faces = await Promise.all([[400, poppinsRegular], [600, poppinsSemiBold]].map(async ([weight, url]) => {
    try {
      return `@font-face{font-family:'Poppins';font-weight:${weight};src:url(${await asDataURL(url)}) format('truetype');}`;
    } catch {
      return '';
    }
  }));
  return faces.join('');
}

const isPie = chart => (chart.series || []).some(series => series.type === 'pie');

const pieSize = chart => {
  const legend = chart.legend || {};
  const paged = legend.pages && legend.pages.length > 1;
  const pagedNames = paged ? Math.max(0, (legend.fullHeight || 0) - (legend.legendHeight || 0)) : 0;
  return { width: chart.chartWidth, height: chart.chartHeight + pagedNames };
};

const legendItems = (chart) => {
  const stacks = (chart.series || []).filter((series) => series.visible && series.options.stacking && _.some(series.points, (point) => point.y));
  return stacks.length > 1 && !_.get(chart, 'options.legend.enabled') ? stacks.map(({ name, color, userOptions }) => ({
    name: _.truncate(name, { length: LEGEND.nameChars }),
    color,
    // Merged options add Highcharts' white border to every column
    edge: userOptions.lineColor || userOptions.borderColor || chartUtils.stateEdge(color, []),
  })) : [];
};

function legendRows(items) {
  const rows = [];
  let used = 0;
  items.forEach((item) => {
    const width = LEGEND.dot + LEGEND.gap + item.name.length * LEGEND.text * LEGEND.charWidth;
    if (!rows.length || used + LEGEND.between + width > CHART_AREA.width) {
      rows.push([]);
      used = -LEGEND.between;
    }
    used += LEGEND.between + width;
    _.last(rows).push({ ...item, width });
  });
  return rows.length > LEGEND.maxRows ? [] : rows;
}

const legendHeight = (rows) => (rows.length ? LEGEND.top + rows.length * LEGEND.row : 0);

function legendMarkup(rows, top) {
  return rows.map((row, index) => {
    const total = _.sumBy(row, 'width') + LEGEND.between * (row.length - 1);
    const y = top + LEGEND.top + index * LEGEND.row + LEGEND.row / 2;
    let x = LEFT + (CHART_AREA.width - total) / 2;
    return row.map((item) => {
      const start = x;
      x += item.width + LEGEND.between;
      return `<circle cx="${start + LEGEND.dot / 2}" cy="${y}" r="${LEGEND.dot / 2 - 0.5}" fill="${escapeXML(item.color)}" stroke="${item.edge}"/>`
        + `<text x="${start + LEGEND.dot + LEGEND.gap}" y="${y + LEGEND.text * 0.35}" font-family="Poppins, sans-serif" font-size="${LEGEND.text}" fill="${GREY}">`
        + `${escapeXML(item.name)}</text>`;
    }).join('');
  }).join('');
}

// (HTML labels can't be drawn into an image)
function chartOptionsForExport(chart, size, extraOptions) {
  const ownAxis = (collection, index) => {
    const own = _.get(chart.options, ['exporting', 'chartOptions', collection]);
    return (Array.isArray(own) ? own[index] : index === 0 && own) || {};
  };
  const axis = collection => (item, index) => Highcharts.merge(ownAxis(collection, index), {
    labels: { style: { color: GREY, fontSize: '12px' } },
    title: { style: { color: GREY, fontSize: '12px', fontWeight: '500' } },
    gridLineColor: GRID,
    lineColor: BORDER,
    tickColor: BORDER,
    plotLines: (item.options.plotLines || []).map(line => Highcharts.merge(
      line,
      line.color === '#444444' ? { color: NAVY, dashStyle: 'Dash', width: 1.5 } : {},
      line.label ? { label: { useHTML: false } } : {}
    ))
  });
  return Highcharts.merge(extraOptions || {}, {
    chart: {
      width: size.width,
      height: size.height,
      backgroundColor: 'transparent',
      style: { fontFamily: 'Poppins, sans-serif' },
      ...(isPie(chart) && chart.spacing ? {
        spacingTop: chart.spacing[0],
        spacingRight: chart.spacing[1],
        spacingBottom: chart.spacing[2],
        spacingLeft: chart.spacing[3]
      } : {})
    },
    title: { text: null },
    subtitle: { text: null },
    credits: { enabled: false },
    // a pie keeps its names' style and shows all of them (an image can't page through them)
    legend: isPie(chart)
      ? { useHTML: false, maxHeight: null, navigation: { enabled: false } }
      : { useHTML: false, itemStyle: { color: GREY, fontSize: '12px', fontWeight: 'normal' } },
    xAxis: chart.xAxis.map(axis('xAxis')),
    yAxis: chart.yAxis.map(axis('yAxis'))
  });
}

const chartSVG = (chart, size, extraOptions) => new Promise((resolve, reject) => {
  chart.getSVGForLocalExport(
    { sourceWidth: size.width, sourceHeight: size.height, scale: 1 },
    chartOptionsForExport(chart, size, extraOptions),
    reject,
    resolve
  );
});

function placeChart(svg, size, area) {
  const scale = Math.min(area.width / size.width, area.height / size.height);
  const width = size.width * scale;
  const height = size.height * scale;
  const x = LEFT + (area.width - width) / 2;
  const y = CHART_TOP + (area.height - height) / 2;
  return svg
    .replace(/^<\?xml[^>]*>/, '')
    .replace(/<svg([^>]*)>/, (tag, attributes) => {
      const kept = attributes.replace(/\s(width|height|x|y|viewBox)="[^"]*"/g, '');
      return `<svg${kept} viewBox="0 0 ${size.width} ${size.height}" x="${x}" y="${y}" width="${width}" height="${height}">`;
    });
}

function compose({
  chart, size, area, legend, title, dashboardName, dateRange, fonts, logo,
}) {
  const context = [
    dashboardName ? `<tspan font-weight="600" fill="${NAVY}">${escapeXML(dashboardName)}</tspan>` : '',
    dashboardName && dateRange ? `<tspan fill="${GREY}"> | </tspan>` : '',
    dateRange ? `<tspan fill="${GREY}">${escapeXML(dateRange)}</tspan>` : ''
  ].join('');
  return `<svg xmlns="http://www.w3.org/2000/svg" xmlns:xlink="http://www.w3.org/1999/xlink" width="${CANVAS.width}" height="${CANVAS.height}" viewBox="0 0 ${CANVAS.width} ${CANVAS.height}">`
    + `<defs><style>${fonts}</style></defs>`
    + `<rect width="${CANVAS.width}" height="${CANVAS.height}" fill="#FFFFFF"/>`
    + `<text x="${LEFT}" y="77" font-family="Poppins, sans-serif" font-size="28" font-weight="600" fill="${NAVY}">${escapeXML(title)}</text>`
    + `<text x="${LEFT}" y="110" font-family="Poppins, sans-serif" font-size="15" xml:space="preserve">${context}</text>`
    + placeChart(chart, size, area)
    + legendMarkup(legend, CHART_TOP + area.height)
    + `<line x1="${LEFT}" x2="${RIGHT}" y1="${FOOTER_LINE + 0.5}" y2="${FOOTER_LINE + 0.5}" stroke="${BORDER}" stroke-width="1"/>`
    + (logo ? `<image href="${logo}" xlink:href="${logo}" x="${LEFT}" y="${FOOTER_MIDDLE - LOGO.height / 2}" width="${LOGO.width}" height="${LOGO.height}"/>` : '')
    + `<text x="${RIGHT}" y="${FOOTER_MIDDLE + 4}" text-anchor="end" font-family="Poppins, sans-serif" font-size="12" fill="${GREY}">Exported ${escapeXML(moment().format('DD MMM YYYY'))}</text>`
    + '</svg>';
}

const rasterize = (svg, type) => new Promise((resolve, reject) => {
  const image = new Image();
  image.onload = () => {
    try {
      const canvas = document.createElement('canvas');
      canvas.width = CANVAS.width * CANVAS.scale;
      canvas.height = CANVAS.height * CANVAS.scale;
      const context = canvas.getContext('2d');
      context.fillStyle = '#FFFFFF';
      context.fillRect(0, 0, canvas.width, canvas.height);
      context.drawImage(image, 0, 0, canvas.width, canvas.height);
      resolve(canvas.toDataURL(type === 'image/jpeg' ? 'image/jpeg' : 'image/png', 0.95));
    } catch (error) {
      reject(error);
    }
  };
  image.onerror = reject;
  image.src = `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
});

function loadJsPDF() {
  const existing = window.jspdf && window.jspdf.jsPDF || window.jsPDF;
  if (existing) return Promise.resolve(existing);
  const libURL = Highcharts.getOptions().exporting.libURL.replace(/\/?$/, '/');
  return new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = `${libURL}jspdf.js`;
    script.onload = () => resolve(window.jspdf && window.jspdf.jsPDF || window.jsPDF);
    script.onerror = reject;
    document.head.appendChild(script);
  });
}

function chartTitle(chart) {
  const exportTitle = _.get(chart, 'options.exporting.chartOptions.title.text');
  if (exportTitle) return exportTitle;
  const box = chart.renderTo && chart.renderTo.closest('.box_white_shadow, .chart_box_view');
  const heading = box && box.querySelector('h3, h1');
  return heading ? heading.textContent.trim() : '';
}

async function downloadComposition(chart, type, extraOptions) {
  const nave = _.get(chart, 'options.exporting.nave');
  const context = (typeof nave === 'function' ? nave() : nave) || {};
  const filename = _.get(chart, 'options.exporting.filename') || 'chart';
  const legend = legendRows(legendItems(chart));
  const area = { width: CHART_AREA.width, height: CHART_AREA.height - legendHeight(legend) };
  const size = isPie(chart) ? pieSize(chart) : area;
  const [svg, fonts, logo] = await Promise.all([
    chartSVG(chart, size, extraOptions),
    fontFaces(),
    asDataURL(naveLogo).catch(() => '')
  ]);
  const composition = compose({
    chart: svg,
    size,
    area,
    legend,
    title: chartTitle(chart),
    dashboardName: context.dashboardName,
    dateRange: context.dateRange,
    fonts,
    logo
  });
  const image = await rasterize(composition, type === 'image/jpeg' ? 'image/jpeg' : 'image/png');
  if (type === 'application/pdf') {
    const JsPDF = await loadJsPDF();
    const pdf = new JsPDF({ orientation: 'landscape', unit: 'pt', format: [PDF_PAGE.width, PDF_PAGE.height] });
    pdf.addImage(image, 'PNG', 0, 0, PDF_PAGE.width, PDF_PAGE.height);
    pdf.save(`${filename}.pdf`);
    return;
  }
  Highcharts.downloadURL(image, `${filename}.${type === 'image/jpeg' ? 'jpg' : 'png'}`);
}

export default async function exportComposition(chart, type, extraOptions) {
  try {
    await downloadComposition(chart, type, extraOptions);
  } catch (error) {
    console.error('[chartExport] The composed image failed, using the chart\'s plain export', error);
    try {
      chart.exportChartLocal({ type }, extraOptions);
    } catch (plainError) {
      console.error('[chartExport] The chart\'s plain export failed', plainError);
    }
  }
}

export { escapeXML };
